const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function safeUuid(id: string): string {
  if (!UUID_RE.test(id)) throw new Error(`Invalid UUID for SQL: ${id}`);
  return id;
}

export const STATS_SQL = `
WITH
  doc_all AS (
    SELECT COUNT(*)::bigint AS job_count, COALESCE(SUM(estimated_cost_usd),0)::numeric AS total_cost,
           COALESCE(AVG(input_tokens+output_tokens),0)::numeric AS avg_tokens, COALESCE(AVG(duration_ms),0)::numeric AS avg_duration_ms
    FROM audit.llm_usage WHERE name = 'document_generation'   ),
  doc_30d AS (
    SELECT COUNT(*)::bigint AS job_count, COALESCE(SUM(estimated_cost_usd),0)::numeric AS total_cost,
           COALESCE(AVG(input_tokens+output_tokens),0)::numeric AS avg_tokens, COALESCE(AVG(duration_ms),0)::numeric AS avg_duration_ms
    FROM audit.llm_usage WHERE name = 'document_generation' AND created_date_utc >= NOW() - INTERVAL '30 days'
  ),
  doc_7d AS (
    SELECT COUNT(*)::bigint AS job_count, COALESCE(SUM(estimated_cost_usd),0)::numeric AS total_cost,
           COALESCE(AVG(input_tokens+output_tokens),0)::numeric AS avg_tokens, COALESCE(AVG(duration_ms),0)::numeric AS avg_duration_ms
    FROM audit.llm_usage WHERE name = 'document_generation' AND created_date_utc >= NOW() - INTERVAL '7 days'
  ),
  emb_all AS (
    SELECT COUNT(*)::bigint AS job_count, COALESCE(SUM(estimated_cost_usd),0)::numeric AS total_cost,
           COALESCE(AVG(input_tokens+output_tokens),0)::numeric AS avg_tokens, COALESCE(AVG(duration_ms),0)::numeric AS avg_duration_ms
    FROM audit.llm_usage WHERE name = 'document_generation_embeddings'   ),
  emb_30d AS (
    SELECT COUNT(*)::bigint AS job_count, COALESCE(SUM(estimated_cost_usd),0)::numeric AS total_cost,
           COALESCE(AVG(input_tokens+output_tokens),0)::numeric AS avg_tokens, COALESCE(AVG(duration_ms),0)::numeric AS avg_duration_ms
    FROM audit.llm_usage WHERE name = 'document_generation_embeddings' AND created_date_utc >= NOW() - INTERVAL '30 days'
  ),
  emb_7d AS (
    SELECT COUNT(*)::bigint AS job_count, COALESCE(SUM(estimated_cost_usd),0)::numeric AS total_cost,
           COALESCE(AVG(input_tokens+output_tokens),0)::numeric AS avg_tokens, COALESCE(AVG(duration_ms),0)::numeric AS avg_duration_ms
    FROM audit.llm_usage WHERE name = 'document_generation_embeddings' AND created_date_utc >= NOW() - INTERVAL '7 days'
  )
SELECT 'all'      AS period, d.job_count AS doc_job_count, d.total_cost AS doc_total_cost, d.avg_tokens AS doc_avg_tokens, d.avg_duration_ms AS doc_avg_duration_ms,
                             e.job_count AS emb_job_count, e.total_cost AS emb_total_cost, e.avg_tokens AS emb_avg_tokens, e.avg_duration_ms AS emb_avg_duration_ms
  FROM doc_all d, emb_all e
UNION ALL
SELECT 'last_30d' AS period, d.job_count, d.total_cost, d.avg_tokens, d.avg_duration_ms, e.job_count, e.total_cost, e.avg_tokens, e.avg_duration_ms
  FROM doc_30d d, emb_30d e
UNION ALL
SELECT 'last_7d'  AS period, d.job_count, d.total_cost, d.avg_tokens, d.avg_duration_ms, e.job_count, e.total_cost, e.avg_tokens, e.avg_duration_ms
  FROM doc_7d  d, emb_7d  e
`;

export type StatsPeriod = "all" | "last_30d" | "last_7d";

export type StatsRow = {
  period: StatsPeriod;
  doc_job_count: number;
  doc_total_cost: number;
  doc_avg_tokens: number;
  doc_avg_duration_ms: number;
  emb_job_count: number;
  emb_total_cost: number;
  emb_avg_tokens: number;
  emb_avg_duration_ms: number;
};

export const DOC_LIST_SQL = `
SELECT
  d.external_id,
  COALESCE(d.name, '(unnamed)')                                        AS name,
  d.status::text                                                       AS status,
  d.model,
  d.created_date_utc,
  d.prompt,
  COALESCE(d.cost_usd, 0)::numeric                                    AS doc_cost_usd,
  length(d.content)::bigint                                            AS content_chars,
  COUNT(ec.external_id)::bigint                                        AS chunk_count,
  COALESCE(SUM(length(ec.content)), 0)::bigint                        AS embedding_chars,
  TRIM(BOTH FROM CONCAT_WS(' ', u.first_name, u.last_name))           AS user_name,
  u.email                                                              AS user_email,
  (LENGTH(d.embeddings_content) - LENGTH(REPLACE(d.embeddings_content, '[TBC', ''))) / 4  AS emb_tbc,
  (LENGTH(d.content)            - LENGTH(REPLACE(d.content,            '[TBC', ''))) / 4  AS doc_tbc,
  (LENGTH(d.embeddings_content) - LENGTH(REPLACE(d.embeddings_content, '[REQUIRED', ''))) / 9 AS emb_req,
  (LENGTH(d.content)            - LENGTH(REPLACE(d.content,            '[REQUIRED', ''))) / 9 AS doc_req
FROM public.document d
LEFT JOIN public.users u         ON u.external_id = d.user_external_id
LEFT JOIN public.embedding e     ON e.document_external_id = d.external_id
LEFT JOIN public.embedding_chunk ec ON ec.embedding_external_id = e.external_id
WHERE d.content IS NOT NULL
  AND d.embeddings_content IS NOT NULL
GROUP BY d.external_id, d.name, d.status, d.model, d.created_date_utc, d.prompt, u.first_name, u.last_name, u.email
ORDER BY d.created_date_utc DESC
LIMIT 200
`;

export type DocListRow = {
  external_id: string;
  name: string;
  status: string;
  model: string;
  created_date_utc: string;
  prompt: string;
  doc_cost_usd: number;
  content_chars: number;
  chunk_count: number;
  embedding_chars: number;
  user_name: string;
  user_email: string;
  emb_tbc: number;
  doc_tbc: number;
  emb_req: number;
  doc_req: number;
};

export function buildDocDetailSql(docId: string | null): string {
  if (docId === null) {
    return `SELECT NULL::text AS doc_content, NULL::text AS embeddings_content, NULL::text AS template_content, NULL::text AS template_name WHERE FALSE`;
  }
  return `
    SELECT
      COALESCE(r.content, d.content)  AS doc_content,
      d.embeddings_content,
      t.template                      AS template_content,
      t.name                          AS template_name
    FROM public.document d
    LEFT JOIN public.revision r
      ON r.document_external_id = d.external_id
     AND r.entity_type = 'document'
     AND r.version = 1
    LEFT JOIN public.template t ON t.external_id = d.template_external_id
    WHERE d.external_id = '${safeUuid(docId)}'::uuid
  `;
}

export type DocDetailRow = {
  doc_content: string;
  embeddings_content: string | null;
  template_content: string | null;
  template_name: string | null;
};

function makeIndexer(columns: string[]) {
  const idx: Record<string, number> = {};
  columns.forEach((c, i) => { idx[c] = i; });
  return (row: unknown[], k: string): unknown => {
    const i = idx[k];
    return i === undefined ? undefined : row[i];
  };
}

const num = (v: unknown): number => (v == null ? 0 : Number(v));
const str = (v: unknown): string => (v == null ? "" : String(v));

export function mapStatsRows(columns: string[], rows: unknown[][]): StatsRow[] {
  const get = makeIndexer(columns);
  return rows.map((r) => ({
    period:              str(get(r, "period")) as StatsPeriod,
    doc_job_count:       num(get(r, "doc_job_count")),
    doc_total_cost:      num(get(r, "doc_total_cost")),
    doc_avg_tokens:      num(get(r, "doc_avg_tokens")),
    doc_avg_duration_ms: num(get(r, "doc_avg_duration_ms")),
    emb_job_count:       num(get(r, "emb_job_count")),
    emb_total_cost:      num(get(r, "emb_total_cost")),
    emb_avg_tokens:      num(get(r, "emb_avg_tokens")),
    emb_avg_duration_ms: num(get(r, "emb_avg_duration_ms")),
  }));
}

export function mapDocListRows(columns: string[], rows: unknown[][]): DocListRow[] {
  const get = makeIndexer(columns);
  return rows.map((r) => ({
    external_id:      str(get(r, "external_id")),
    name:             str(get(r, "name")),
    status:           str(get(r, "status")),
    model:            str(get(r, "model")),
    created_date_utc: str(get(r, "created_date_utc")),
    prompt:           str(get(r, "prompt")),
    doc_cost_usd:     num(get(r, "doc_cost_usd")),
    content_chars:    num(get(r, "content_chars")),
    chunk_count:      num(get(r, "chunk_count")),
    embedding_chars:  num(get(r, "embedding_chars")),
    user_name:        str(get(r, "user_name")),
    user_email:       str(get(r, "user_email")),
    emb_tbc:          num(get(r, "emb_tbc")),
    doc_tbc:          num(get(r, "doc_tbc")),
    emb_req:          num(get(r, "emb_req")),
    doc_req:          num(get(r, "doc_req")),
  }));
}

export function mapDocDetailRows(columns: string[], rows: unknown[][]): DocDetailRow[] {
  const get = makeIndexer(columns);
  return rows.map((r) => ({
    doc_content:        str(get(r, "doc_content")),
    embeddings_content: get(r, "embeddings_content") == null ? null : str(get(r, "embeddings_content")),
    template_content:   get(r, "template_content")   == null ? null : str(get(r, "template_content")),
    template_name:      get(r, "template_name")      == null ? null : str(get(r, "template_name")),
  }));
}
